import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ContentLanguage } from "@/stores/language";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return "0:00";
  const totalSecs = Math.floor(seconds);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
  return then.toLocaleDateString();
}

/**
 * Xiaohongshu share short-link host, as a regex fragment.
 * XHS rotates the TLD on its share links (historically `.com`, now `.cn`),
 * so every place that recognizes an xhslink short link references this one
 * fragment — add a new TLD here and every consumer picks it up.
 */
export const XHS_SHORTLINK_HOST = String.raw`xhslink\.(?:com|cn)`;

/**
 * Extract a supported video URL from share text or a raw URL.
 * Supports Xiaohongshu share text and YouTube links.
 */
export function extractVideoUrl(input: string): string | null {
  const patterns = [
    // Xiaohongshu (preserve query params — xsec_token is needed for PC web shares)
    /https?:\/\/(?:www\.)?xiaohongshu\.com\/(?:explore|discovery\/item)\/[a-zA-Z0-9]+(?:\?[^\s]*)?/,
    new RegExp(String.raw`https?://(?:www\.)?${XHS_SHORTLINK_HOST}/[a-zA-Z0-9/]+`),
    // YouTube
    /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]{11}(?:[^\s]*)?/,
    /https?:\/\/(?:www\.)?youtube\.com\/shorts\/[a-zA-Z0-9_-]{11}(?:[^\s]*)?/,
    /https?:\/\/youtu\.be\/[a-zA-Z0-9_-]{11}(?:\?[^\s]*)?/,
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[0];
  }
  return null;
}

export type VideoPlatform = "xiaohongshu" | "youtube";

export function detectPlatform(input: string): VideoPlatform | null {
  const url = extractVideoUrl(input);
  if (!url) return null;
  if (new RegExp(String.raw`xiaohongshu\.com|${XHS_SHORTLINK_HOST}`).test(url))
    return "xiaohongshu";
  if (/youtube\.com|youtu\.be/.test(url)) return "youtube";
  return null;
}

export function isValidVideoUrl(input: string): boolean {
  return extractVideoUrl(input) !== null;
}

/**
 * Videos the user uploaded from their own device, as opposed to ones fetched
 * from a platform by URL. `source_url` is NOT NULL and indexed, so uploads
 * carry a synthetic `upload://<video id>` there — the same trick the
 * `recovered://` imports use. Anything that treats sourceUrl as a link the
 * user could open must check isUploadSourceUrl() first.
 */
export const UPLOAD_PLATFORM = "upload";

export function uploadSourceUrl(videoId: string): string {
  return `upload://${videoId}`;
}

export function isUploadSourceUrl(sourceUrl: string | null | undefined): boolean {
  return !!sourceUrl && sourceUrl.startsWith("upload://");
}

/** "512 MB", "1.2 GB" — for size limits and file pickers. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export function extractVideoId(input: string): string | null {
  const url = extractVideoUrl(input);
  if (!url) return null;

  const patterns = [
    // Xiaohongshu
    /xiaohongshu\.com\/explore\/([a-zA-Z0-9]+)/,
    /xiaohongshu\.com\/discovery\/item\/([a-zA-Z0-9]+)/,
    new RegExp(String.raw`${XHS_SHORTLINK_HOST}/(?:[a-zA-Z0-9]+/)*([a-zA-Z0-9]+)/?$`),
    // YouTube
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

/** Pick the best display title for a video based on language */
export function getVideoDisplayTitle(
  video: { dishNameChinese?: string | null; dishNameEnglish?: string | null; titleOriginal: string; titleEnglish?: string | null },
  lang: ContentLanguage
): string {
  return lang === "en"
    ? video.dishNameEnglish ?? video.titleEnglish ?? video.titleOriginal
    : video.dishNameChinese ?? video.titleOriginal;
}

// Keep old names as aliases for backward compat
export const extractXhsUrl = extractVideoUrl;
export const isValidXiaohongshuUrl = isValidVideoUrl;
export const extractXhsVideoId = extractVideoId;

/**
 * Pick tags matching the current display language. Tags are normally generated
 * as alternating Chinese/English pairs (["家常菜","home-style",…]) but some
 * older rows have combined strings like "家常菜 / home-style" — for those we
 * extract just the Chinese or English portion instead of dropping the tag.
 * Result is deduped so pair-generated tags don't double up after extraction.
 */
export function extractTagsForLang(tags: string[], lang: ContentLanguage): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of tags) {
    const extracted = extractTagForLang(raw, lang);
    if (extracted && !seen.has(extracted)) {
      seen.add(extracted);
      out.push(extracted);
    }
  }
  return out;
}

function extractTagForLang(tag: string, lang: ContentLanguage): string | null {
  const trimmed = tag.trim();
  if (!trimmed) return null;
  const hasChinese = /[一-鿿]/.test(trimmed);
  const hasLatin = /[A-Za-z]/.test(trimmed);
  if (hasChinese && !hasLatin) return lang === "zh" ? trimmed : null;
  if (hasLatin && !hasChinese) return lang === "en" ? trimmed : null;
  // Hybrid — extract the portion matching the requested language.
  if (lang === "zh") {
    const chinese = trimmed.match(/[一-鿿]+/g)?.join("") ?? "";
    return chinese || null;
  }
  const english = trimmed
    .replace(/[一-鿿]+/g, " ")
    .replace(/[\/()\[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return english || null;
}
